import { useState, useRef, useEffect } from 'react';

interface CategorySectionProps {
  categories: { name: string; url: string; icon: string; image: string }[];
}

export default function CategorySection({ categories }: CategorySectionProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="py-14 md:py-20 bg-background-50" id="categories">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Browse Product Categories
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
            Select a category below or use the dropdown to explore products organized in the Basetao Spreadsheet
          </p>
        </div>

        <div className="relative mb-10 max-w-md mx-auto" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl bg-background-100 border border-background-200/70 text-foreground-700 hover:border-background-300/60 transition-colors cursor-pointer"
          >
            <span className="text-sm font-medium">Select a product category...</span>
            <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {dropdownOpen && (
            <div className="absolute top-full mt-2 left-0 right-0 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-72 overflow-y-auto z-40 animate-[fadeIn_0.15s_ease-out]">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                >
                  <img src={cat.image} alt={cat.name} className="w-7 h-7 rounded-md object-cover flex-shrink-0" />
                  {cat.name}
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col rounded-xl overflow-hidden bg-background-100 hover:bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer hover:-translate-y-0.5"
            >
              <div className="aspect-square w-full overflow-hidden">
                <img
                  src={cat.image}
                  alt={`${cat.name} category - Basetao Spreadsheet`}
                  title={`${cat.name} - Basetao Spreadsheet Products`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-3 flex items-center justify-center gap-1.5">
                <i className={`${cat.icon} text-foreground-500 group-hover:text-primary-600 text-xs`}></i>
                <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-700 text-center leading-tight whitespace-nowrap">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}